import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { SimpleOrganization } from '@app/src/types/organizations'
import { SolutionType } from '@app/src/types/resourceExplorer'
import type { OrganizationSettings } from '@app/src/wf-constants'
import { NotificationSeverity, ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

type CsmOrganizationSettingsBody = {
  organizationId: number
  solutionType: SolutionType
  patchSettings: Partial<OrganizationSettings>
}

interface UseCsmOrganizationSettingsOptions {
  closeDrawer: () => void
  resetForm: () => void
  solutionType?: SolutionType
}

/**
 * Imperative-shell hook shared by the CSM organization-settings drawers.
 * Loads the current settings for the organization's solution and exposes a `save` that patches
 * them, handling cache invalidation, success/error notifications, form reset and closing the drawer.
 * Callers only decide which fields to patch (e.g. AI screenings vs contracted users).
 */
export const useCsmOrganizationSettings = (
  organization: SimpleOrganization,
  { closeDrawer, resetForm, solutionType: requestedSolutionType }: UseCsmOrganizationSettingsOptions,
) => {
  const { formatMessage } = useIntl()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isSaving } = useUpdateResource<CsmOrganizationSettingsBody>()

  const solutionType = requestedSolutionType ?? organization.setting?.solutionType ?? SolutionType.Default

  const { data: settings } = useFetchResource<OrganizationSettings>({
    key: [FetchKey.OrganizationCsmSettingsById, organization.id, solutionType],
    endpoint: endpoints.organizationCsmSettingsById(organization.id, solutionType),
    options: { enabled: Boolean(organization.id), staleTime: ONE_MINUTE_MS },
  })

  const save = (patchSettings: Partial<OrganizationSettings>) =>
    mutateAsync(
      {
        url: endpoints.organizationCsmSettings,
        body: { organizationId: organization.id, solutionType, patchSettings },
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
          resetForm()
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationEntitlements, organization.id] })
          queryClient.invalidateQueries({
            queryKey: [FetchKey.OrganizationCsmSettingsById, organization.id, solutionType],
          })
          closeDrawer()
          resetForm()
          showSnackbar({
            message: formatMessage({ id: 'general.organizationSettingsSaved' }),
            severity: NotificationSeverity.success,
          })
        },
      },
    )

  return { settings, solutionType, save, isSaving }
}
